import { format } from "date-fns";
import { PartOrder, PartOrderItemStatus, PartOrderItemV2, PartOrderStatus } from "../../generated/nest-graphql";

/**
 * Pickup state, derived — never stored at order level.
 *
 * The only file that interprets item statuses. `Collected` means collected; `On_Van` is
 * the dead value that used to; `Picked_Up` means the OPPOSITE of its name and is legacy.
 */

/** An item is "collected" only once a technician has said so; same rule as ops-backend's `hasPickedUpPart`. */
export const isCollected = (item?: PartOrderItemV2 | null): boolean =>
  item?.status === PartOrderItemStatus.Collected || item?.status === PartOrderItemStatus.OnVan || !!item?.pickedUpAt;

/** Everything else on an active order is still waiting at the store. */
export const isAwaitingPickup = (item?: PartOrderItemV2 | null): boolean =>
  !!item && !isCollected(item) && item.status !== PartOrderItemStatus.Returned;

export type OrderPickupState = "AWAITING" | "PARTIAL" | "COLLECTED" | "CANCELED" | "EMPTY";

export type OrderPickupSummary = {
  state: OrderPickupState;
  collected: number;
  total: number;
};

/**
 * Rolls an order's items up into one state. A canceled order reports as canceled
 * whatever its items say — ops owns that flag and it outranks anything a pickup does.
 */
export const summarisePickup = (order: PartOrder): OrderPickupSummary => {
  const items = (order?.items ?? []).filter(Boolean) as PartOrderItemV2[];
  const collected = items.filter(isCollected).length;
  const total = items.length;

  if (order?.status === PartOrderStatus.Canceled) return { state: "CANCELED", collected, total };
  if (total === 0) return { state: "EMPTY", collected, total };
  if (collected === 0) return { state: "AWAITING", collected, total };
  if (collected === total) return { state: "COLLECTED", collected, total };
  return { state: "PARTIAL", collected, total };
};

export const orderPickupLabel = ({ state, collected, total }: OrderPickupSummary): string => {
  switch (state) {
    case "CANCELED":
      return "Canceled";
    case "COLLECTED":
      return "Picked up";
    case "PARTIAL":
      return `Partially picked up (${collected} of ${total})`;
    case "EMPTY":
      return "No parts listed";
    default:
      return "Not picked up";
  }
};

export type PartsFilter = "ALL" | "AWAITING" | "COLLECTED";

/**
 * An order matches a filter when at least one of its items does, so a partially
 * collected order shows under both "Ordered" and "Picked up" — which is the
 * honest answer: the technician has something to collect there and something already
 * loaded. Canceled orders only appear under "All", unless a withdrawn job's parts need returning.
 */
export const orderMatchesFilter = (order: PartOrder, filter: PartsFilter): boolean => {
  const items = (order?.items ?? []).filter(Boolean) as PartOrderItemV2[];
  // The backend only sends a withdrawn job's order once a part was picked up; it is there to be
  // returned, so it shows under Picked up even if ops canceled the order meanwhile.
  if (order?.job?.isWithdrawn) return filter === "COLLECTED" && items.some(isCollected);
  if (filter === "ALL") return true;
  if (order?.status === PartOrderStatus.Canceled) return false;

  return filter === "COLLECTED" ? items.some(isCollected) : items.some(isAwaitingPickup);
};

/** Whether a job's orders are all on a withdrawn job; null until any of its orders has loaded. */
export const isWithdrawnJob = (orders: PartOrder[], jobId: string): boolean | null => {
  const jobOrders = orders.filter((order) => order?.job?.id === jobId);
  return jobOrders.length ? jobOrders.every((order) => !!order?.job?.isWithdrawn) : null;
};

/** A stable, human key for grouping — orders with no store still need a bucket. */
export const storeKeyOf = (order: PartOrder): string => order?.store?.id ?? "__no_store__";

export type StoreGrouping = {
  key: string;
  name: string;
  address?: string | null;
  vendor?: string | null;
  orders: PartOrder[];
};

/**
 * Groups orders by store, then orders each group by order number so a technician
 * walking up to a counter reads them in the same sequence every time. Stores are
 * sorted by name, with the "no store recorded" bucket forced last.
 */
export const groupByStore = (orders: PartOrder[]): StoreGrouping[] => {
  const groups = new Map<string, StoreGrouping>();

  for (const order of orders) {
    const key = storeKeyOf(order);
    const existing = groups.get(key);
    if (existing) {
      existing.orders.push(order);
      continue;
    }
    groups.set(key, {
      key,
      name: order?.store?.name ?? "No store recorded",
      address: order?.store?.address,
      vendor: order?.store?.vendor,
      orders: [order],
    });
  }

  const sorted = Array.from(groups.values()).sort((a, b) => {
    if (a.key === "__no_store__") return 1;
    if (b.key === "__no_store__") return -1;
    return a.name.localeCompare(b.name);
  });

  for (const group of sorted) {
    group.orders.sort((a, b) => (a?.orderNumber ?? "").localeCompare(b?.orderNumber ?? ""));
  }

  return sorted;
};

/**
 * The arrival window on the order's own job. Prefers the customer-facing time window
 * over the appointment's own bounds, the same way the schedule does. Shared so the
 * card and the "by job" group header can never disagree about a job's time.
 */
export const jobWindowOf = (order: PartOrder): { start: Date | null; end: Date | null } => {
  const appointment = order?.job?.appointment;
  const rawStart = appointment?.timeWindow?.startTimeWindow ?? appointment?.startDate;
  const rawEnd = appointment?.timeWindow?.endTimeWindow ?? appointment?.endDate;
  return {
    start: rawStart ? new Date(rawStart) : null,
    end: rawEnd ? new Date(rawEnd) : null,
  };
};

export const formatPickedUpAt = (item?: PartOrderItemV2 | null): string =>
  item?.pickedUpAt ? format(new Date(item.pickedUpAt), "h:mmaaa") : "";

export const formatJobWindow = (start?: Date | null, end?: Date | null): string =>
  start && end ? `${format(start, "EEE MMM d")} | ${format(start, "h:mmaaa")} - ${format(end, "h:mmaaa")}` : "";

export type PartsGrouping = "STORE" | "JOB";

/** A stable key for the job bucket — orders with no job still need one. */
export const jobKeyOf = (order: PartOrder): string => order?.job?.id ?? "__no_job__";

export type JobGrouping = {
  key: string;
  jobId?: string | null;
  jobNumber?: string | null;
  vehicle: string;
  start: Date | null;
  end: Date | null;
  orders: PartOrder[];
};

/**
 * Groups orders by job, earliest job first, so the list reads as the technician's day
 * rather than as a driving route. Jobs with no appointment have no place in that
 * sequence, so they fall to the bottom rather than sorting as epoch 0. Orders inside a
 * job keep the same order-number sequence used everywhere else.
 */
export const groupByJob = (orders: PartOrder[]): JobGrouping[] => {
  const groups = new Map<string, JobGrouping>();

  for (const order of orders) {
    const key = jobKeyOf(order);
    const existing = groups.get(key);
    if (existing) {
      existing.orders.push(order);
      continue;
    }
    const vehicleInfo = order?.job?.vehicleInfo;
    const { start, end } = jobWindowOf(order);
    groups.set(key, {
      key,
      jobId: order?.job?.id,
      jobNumber: order?.job?.jobNumber,
      vehicle: vehicleInfo?.year
        ? `${vehicleInfo.year} ${vehicleInfo.make ?? ""} ${vehicleInfo.model ?? ""}`.trim()
        : "",
      start,
      end,
      orders: [order],
    });
  }

  const sorted = Array.from(groups.values()).sort((a, b) => {
    if (a.start && b.start) {
      const diff = a.start.getTime() - b.start.getTime();
      if (diff !== 0) return diff;
    } else if (a.start !== b.start) {
      // Unscheduled last, whichever side it is on.
      return a.start ? -1 : 1;
    }
    return (a.jobNumber ?? "").localeCompare(b.jobNumber ?? "");
  });

  for (const group of sorted) {
    group.orders.sort((a, b) => (a?.orderNumber ?? "").localeCompare(b?.orderNumber ?? ""));
  }

  return sorted;
};

/** Reason codes ops already uses. Kept identical so both surfaces report the same set. */
export const PART_ORDERING_ISSUE_OPTIONS = [
  "Wrong parts ordered by us",
  "Some parts not ordered",
  "Store never received order",
  "Wrong parts ordered by store",
  "Unnecessary parts ordered",
  "Parts misboxed",
  "Order placed too late",
  "Parts not pulled in time",
  "AA/OR substituted parts",
];
